import React from 'react';
import { toast } from 'react-toastify';
import { FormattedMessage } from 'react-intl';
import { ReactComponent as Success } from 'icons/success.svg';
import { ReactComponent as Warning } from 'icons/warning.svg';
import {
  AUTO_TIEOUT_COMPARE_MODE,
  SIDE_BY_SIDE_COMPARE_MODE,
  STATEMENT_CONTENT_PANEL_ZOOM_BLOCK,
  STATEMENT_CONTENT_PANEL_ZOOM_LEFT_BLOCK,
  OMNIA_TB_UPDATE_ALERT,
} from './statement-content-constants';
import { ROUTE_CONSTANTS } from 'constants/util/route-constants';

export const ANNOTATION_MAP_ACTION_BLOCK = 'annotation-map-action';
const BANNER_ICON_SIZE = '10';

export const getExcessiveElementSelectionNotification = (elementLimit) => {
  return toast.error(
    <div>
      <Warning
        name={'warning'}
        width={BANNER_ICON_SIZE}
        height={BANNER_ICON_SIZE}
      />
      <div className={`${ANNOTATION_MAP_ACTION_BLOCK}__notify-header`}>
        <strong>
          <FormattedMessage
            values={{
              numberOfElements: elementLimit,
            }}
            id="element-panel.annotation.content.element-map-notify"
          />
        </strong>
      </div>
    </div>,
    { autoClose: 5000 },
  );
};

// this function is used to segregate the source and current(target) elements from array of objects based on elementUpdateStatus property to identify success and failed mapped elements
export const seggregateSourceTargetElements = (elementsMapped) => {
  let successSourceElements = [];
  let failedSourceElements = [];
  let successTargetElements = [];
  let failedTargetElements = [];
  for (let i = 0; i < elementsMapped.length; i++) {
    if (elementsMapped[i].elementUpdateStatus) {
      successSourceElements.push(elementsMapped[i].sourceElementId);
      successTargetElements.push(elementsMapped[i].targetElementId);
    } else {
      failedSourceElements.push(elementsMapped[i].sourceElementId);
      failedTargetElements.push(elementsMapped[i].targetElementId);
    }
  }
  return {
    successSourceElements,
    failedSourceElements,
    successTargetElements,
    failedTargetElements,
  };
};

//successful notification for element(s) mapped
export const successElementMapNotification = ({
  processType,
  targetElements,
}) => {
  switch (processType) {
    case SIDE_BY_SIDE_COMPARE_MODE:
      return toast.success(
        <div>
          <Success
            name={'success'}
            width={BANNER_ICON_SIZE}
            height={BANNER_ICON_SIZE}
          />
          <div className={`${ANNOTATION_MAP_ACTION_BLOCK}__success-header`}>
            <strong>
              <FormattedMessage
                id="element-panel.annotation.content.element-map-sucess"
                values={{
                  br: (
                    <br
                      key={`${ANNOTATION_MAP_ACTION_BLOCK}__success-header-brTag`}
                    />
                  ),
                }}
              />
            </strong>
          </div>
          <FormattedMessage
            values={{
              numberOfElements: targetElements.length,
              br: (
                <br
                  key={`${ANNOTATION_MAP_ACTION_BLOCK}__success-summary-brTag`}
                />
              ),
            }}
            id="element-panel.annotation.content.element-map-sucess-summary"
          />
        </div>,
        { autoClose: 5000 },
      );
    case AUTO_TIEOUT_COMPARE_MODE:
      return toast.success(
        <div>
          <Success
            name={'success'}
            width={BANNER_ICON_SIZE}
            height={BANNER_ICON_SIZE}
          />
          <FormattedMessage
            id={'side-by-side-view.compare.auto-tieout-success.notification'}
          />
        </div>,
        { autoClose: 4000 },
      );
    default:
      return null;
  }
};

//failure notification for element(s) mapped
export const failureElementMapNotification = ({
  processType,
  targetElements,
}) => {
  switch (processType) {
    case SIDE_BY_SIDE_COMPARE_MODE:
      return toast.error(
        <div>
          <Warning
            name={'warning'}
            width={BANNER_ICON_SIZE}
            height={BANNER_ICON_SIZE}
          />
          <div className={`${ANNOTATION_MAP_ACTION_BLOCK}__fail-header`}>
            <strong>
              <FormattedMessage
                id="element-panel.annotation.content.element-map-fail"
                values={{
                  br: (
                    <br
                      key={`${ANNOTATION_MAP_ACTION_BLOCK}__fail-header-brTag`}
                    />
                  ),
                }}
              />
            </strong>
          </div>
          <FormattedMessage
            values={{
              numberOfElements: targetElements.length,
              br: (
                <br
                  key={`${ANNOTATION_MAP_ACTION_BLOCK}__fail-summary-brTag`}
                />
              ),
            }}
            id="element-panel.annotation.content.element-map-fail-summary"
          />
        </div>,
        { autoClose: 5000 },
      );
    case AUTO_TIEOUT_COMPARE_MODE:
      return toast.error(
        <div>
          <Warning
            name={'warning'}
            width={BANNER_ICON_SIZE}
            height={BANNER_ICON_SIZE}
          />
          <FormattedMessage id="side-by-side-view.compare.auto-tieout-failure.notification" />
        </div>,
        { autoClose: 4000 },
      );
    default:
      return null;
  }
};

export const alertNotification = ({ processType }) => {
  switch (processType) {
    case OMNIA_TB_UPDATE_ALERT:
      return toast.warning(
        <div>
          <Warning
            name={'warning'}
            width={BANNER_ICON_SIZE}
            height={BANNER_ICON_SIZE}
          />
          <FormattedMessage id="trial-balance-view.omnia.tb.update.alert" />
        </div>,
        { autoClose: 5000 },
      );
    default:
      return null;
  }
};

export const getCalloutPosition = ({
  position,
  calloutRef,
  id,
  zoom,
  leftSideView,
}) => {
  const zoomPercentageSubtractionLR = 10;
  const zoomPercentageSubtractionTB = 8;
  const bodyPadding = 5;
  let calloutPosition = position;

  const calloutBoundingInfo =
    (calloutRef &&
      calloutRef.current &&
      calloutRef.current.getBoundingClientRect()) ||
    {};
  const { height: calloutHeight, width: calloutWidth } = calloutBoundingInfo;

  const div = calloutRef && calloutRef.current;
  const afterStyles = div && window.getComputedStyle(div, '::after');
  const afterContentWidth =
    afterStyles &&
    afterStyles.getPropertyValue('width') &&
    parseInt(afterStyles.getPropertyValue('width').split('p')[0]);
  const afterContentHeight =
    afterStyles &&
    afterStyles.getPropertyValue('height') &&
    parseInt(afterStyles.getPropertyValue('height').split('p')[0]);

  const documentId = leftSideView
    ? STATEMENT_CONTENT_PANEL_ZOOM_LEFT_BLOCK
    : STATEMENT_CONTENT_PANEL_ZOOM_BLOCK;
  const parentElement = document && document.getElementById(documentId);

  const {
    left: parentLeftPosition,
    top: parentTopPosition,
    right: parentRightPosition,
  } = parentElement ? parentElement.getBoundingClientRect() : {};

  const tieoutElement = document.getElementById(id);
  const { right, top, height, left, width } = tieoutElement
    ? tieoutElement.getBoundingClientRect()
    : {};
  const elementCenter = height / 2;
  const calloutCenter = calloutHeight / 2;

  let value;
  if (position === 'left')
    value = left - parentLeftPosition - calloutWidth + bodyPadding;
  if (position === 'right')
    value = parentRightPosition - right - calloutWidth + bodyPadding;
  let callout = {};

  if (position === 'left' && value < 0) {
    calloutPosition = 'right';
  }
  if (position === 'right' && value < 0) {
    calloutPosition = 'left';
  }

  const customWidth = (width - calloutWidth) / 2;

  switch (calloutPosition) {
    case 'left':
      callout = {
        left:
          left &&
          afterContentWidth &&
          `${
            (left - parentLeftPosition - calloutWidth + bodyPadding) /
              zoom.zoomRatio -
            zoomPercentageSubtractionLR * zoom.zoomRatio
          }px`,
        top:
          top &&
          `${
            (top - parentTopPosition - calloutCenter + elementCenter) /
            zoom.zoomRatio
          }px`,
      };
      break;
    case 'right':
      callout = {
        right:
          right &&
          `${
            (parentRightPosition - right - calloutWidth + bodyPadding) /
              zoom.zoomRatio -
            zoomPercentageSubtractionLR * zoom.zoomRatio
          }px`,
        top:
          top &&
          `${
            (top - parentTopPosition - calloutCenter + elementCenter) /
            zoom.zoomRatio
          }px`,
      };
      break;
    case 'top':
      callout = {
        left:
          left &&
          `${(left - parentLeftPosition + customWidth) / zoom.zoomRatio}px`,
        top: `${
          (top -
            parentTopPosition -
            calloutHeight +
            bodyPadding +
            -afterContentHeight) /
            zoom.zoomRatio -
          zoomPercentageSubtractionTB * zoom.zoomRatio
        }px`,
      };
      break;
    case 'bottom':
      callout = {
        left:
          left &&
          `${(left - parentLeftPosition + customWidth) / zoom.zoomRatio}px`,
        top: `${
          (top -
            parentTopPosition +
            height -
            bodyPadding -
            -afterContentHeight) /
            zoom.zoomRatio +
          zoomPercentageSubtractionTB * zoom.zoomRatio
        }px`,
      };
      break;

    default:
      break;
  }

  return callout;
};

export const getMarkerPosition = ({
  markerRef,
  position,
  id,
  zoom,
  leftSideView,
}) => {
  const documentId = leftSideView
    ? STATEMENT_CONTENT_PANEL_ZOOM_LEFT_BLOCK
    : STATEMENT_CONTENT_PANEL_ZOOM_BLOCK;
  const parentElement = document && document.getElementById(documentId);
  const {
    left: parentLeftPosition,
    top: parentTopPosition,
    right: parentRightPosition,
  } = parentElement ? parentElement.getBoundingClientRect() : {};

  const tieoutElement = document.getElementById(id);

  const { right, top, left, width, height } = tieoutElement
    ? tieoutElement.getBoundingClientRect()
    : {};

  const annotationMarkerInfo =
    (markerRef &&
      markerRef.current &&
      markerRef.current.getBoundingClientRect()) ||
    {};
  const { width: annotationMarkerElementWidth } = annotationMarkerInfo;

  const elementHorizontalCenter = width / 2;

  const customHeight = (height - annotationMarkerElementWidth) / 2;

  let marker = {};

  switch (position) {
    case 'left':
      marker = {
        left:
          left &&
          `${
            (left - parentLeftPosition - annotationMarkerElementWidth) /
            zoom.zoomRatio
          }px`,
        top:
          top &&
          `${(top - parentTopPosition + customHeight) / zoom.zoomRatio}px`,
      };
      break;
    case 'right':
      marker = {
        right:
          right &&
          `${
            (parentRightPosition - right - annotationMarkerElementWidth) /
            zoom.zoomRatio
          }px`,
        top:
          top &&
          `${(top - parentTopPosition + customHeight) / zoom.zoomRatio}px`,
      };
      break;
    case 'top':
      marker = {
        left:
          left &&
          `${
            (left -
              parentLeftPosition +
              elementHorizontalCenter -
              annotationMarkerElementWidth / 2) /
            zoom.zoomRatio
          }px`,
        top: `${
          (top - parentTopPosition - annotationMarkerElementWidth) /
          zoom.zoomRatio
        }px`,
      };
      break;
    case 'bottom':
      marker = {
        left:
          left &&
          `${
            (left -
              parentLeftPosition +
              elementHorizontalCenter -
              annotationMarkerElementWidth / 2) /
            zoom.zoomRatio
          }px`,
        top: `${
          (top - parentTopPosition + annotationMarkerElementWidth) /
          zoom.zoomRatio
        }px`,
      };
      break;

    default:
      break;
  }

  return marker;
};

export const setCalloutArrowDynamicWidth = ({
  tieoutElement,
  fontSize,
  position,
}) => {
  const scalingFactor = 0.05;
  const dynamicWidthBefore =
    tieoutElement &&
    tieoutElement.offsetHeight * scalingFactor * fontSize + 'px';

  const dynamicWidthAfter =
    tieoutElement &&
    tieoutElement.offsetHeight * scalingFactor * fontSize - 1 + 'px';

  tieoutElement &&
    tieoutElement.style.setProperty(
      `--${position}-arrow-width-before`,
      dynamicWidthBefore,
    );

  tieoutElement &&
    tieoutElement.style.setProperty(
      `--${position}-arrow-width-after`,
      dynamicWidthAfter,
    );

  return { dynamicWidthBefore, dynamicWidthAfter };
};

export const removeObjectsByOrdinal = (tickmarklist, ordinalsString) => {
  if (!ordinalsString || ordinalsString.length === 0) {
    return tickmarklist;
  }
  const ordinalsToRemove = (ordinalsString.match(/\{([^{}]+)\}/g) || []).map(
    (match) => match.replace(/[{}]/g, ''),
  );

  const ordinalsSet = new Set(ordinalsToRemove);

  if (ordinalsSet.size === 0) {
    return objectList;
  }

  const filteredList = tickmarklist.filter((obj) => {
    return !ordinalsSet.has(obj.noteOrdinal);
  });

  return filteredList;
};

// Helper function to check if element should be hidden based on workflow completion
const shouldHideElement = ({
  workFlowStatus,
  leftView,
  mappedElements,
  elementId,
  isAutoTieoutMode,
}) => {
  if (isAutoTieoutMode) {
    const isElementsActive = leftView
      ? mappedElements?.isSourceElementActive(elementId)
      : mappedElements?.isTargetElementActive(elementId);
    return workFlowStatus?.isCompleted && isElementsActive;
  }
  return (
    workFlowStatus?.isCompleted &&
    !leftView &&
    mappedElements?.isTargetElementActive(elementId)
  );
};

export const handleShowCrossIconByStmtWrappedUp = ({
  isStatementWrappedOrPendingApproval = false,
  isTrialBalanceMode = false,
  workFlowStatus = {},
  isAutoTieoutMode = false,
  leftView = false,
  mappedElements,
  elementDetails,
  isReadOnlyOverride = false,
  editPermissionAutotieout,
}) => {
  if (!window.location.pathname.includes(ROUTE_CONSTANTS.SIDE_BY_SIDE)) {
    return false;
  }

  if (isReadOnlyOverride || !editPermissionAutotieout) return false; // If statement is previous revision, do not show cross icon in auto tieout view.

  if (isTrialBalanceMode || isAutoTieoutMode) {
    return !(
      isStatementWrappedOrPendingApproval &&
      shouldHideElement({
        workFlowStatus,
        leftView,
        mappedElements,
        elementId: elementDetails?.id,
      })
    );
  }

  return false;
};

export const handleShowSelectionCursorForMappedElements = ({
  isStatementWrappedOrPendingApproval = false,
  isTrialBalanceMode = false,
  workFlowStatus = {},
  isAutoTieoutMode = false,
  leftView = false,
  mappedElements,
  elementDetails,
}) => {
  if (!window.location.pathname.includes(ROUTE_CONSTANTS.SIDE_BY_SIDE)) {
    return false;
  }

  if (!isStatementWrappedOrPendingApproval) {
    return false;
  }

  if (isTrialBalanceMode || isAutoTieoutMode) {
    return !shouldHideElement({
      workFlowStatus,
      leftView,
      mappedElements,
      elementId: elementDetails?.id,
      isAutoTieoutMode,
    });
  }

  return false;
};

export const handleHighlightMappedElements = ({
  isSideBySideView,
  highlightMappedElements,
  isAutoTieoutMode,
  isTrialBalanceMode,
  isLeftView,
  elementDetails,
  autoTieoutMappedElementDetails,
}) => {
  if (!isSideBySideView || !highlightMappedElements) {
    return false;
  }

  const elementId = elementDetails?.id;
  if (!elementId || !autoTieoutMappedElementDetails) {
    return false;
  }

  let mappedElementId;

  if (isAutoTieoutMode) {
    mappedElementId = isLeftView
      ? autoTieoutMappedElementDetails?.isMappedElementExistsInSourceMap?.(
          elementId,
        )
      : autoTieoutMappedElementDetails?.isMappedElementExistsInTargetMap?.(
          elementId,
        );
  } else if (isTrialBalanceMode) {
    mappedElementId =
      autoTieoutMappedElementDetails?.isElementMapped?.(elementId);
  } else {
    return false;
  }

  return elementId === mappedElementId;
};
